import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes, deleteObject } from "firebase/storage";
import { db, storage } from "./firebase";

export type LaunchSplashStatus = "active" | "inactive";

export interface LaunchSplashImage {
  id: string;
  url: string;
  status: LaunchSplashStatus;
  altText?: string | null;
  routePath?: string | null;
  storagePath?: string | null;
  fileName?: string | null;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string | null;
  width?: number | null;
  height?: number | null;
}

const COLLECTION = "launchSplashImages";
const SUPPORTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const mapDocToImage = (docSnap: any): LaunchSplashImage => {
  const data = docSnap.data() || {};
  return {
    id: docSnap.id,
    url: data.url,
    status: data.status || "inactive",
    altText: data.altText ?? null,
    routePath: data.routePath ?? null,
    storagePath: data.storagePath ?? null,
    fileName: data.fileName ?? null,
    createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : undefined,
    updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : undefined,
    createdBy: data.createdBy ?? null,
    width: data.width ?? null,
    height: data.height ?? null,
  };
};

const getImageDimensions = (file: File): Promise<{ width: number; height: number } | null> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const dimensions = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(img.src);
      resolve(dimensions);
    };
    img.onerror = () => resolve(null);
    img.src = URL.createObjectURL(file);
  });
};

export const subscribeToLaunchSplashImages = (
  callback: (images: LaunchSplashImage[]) => void
): (() => void) => {
  const splashRef = collection(db, COLLECTION);
  const splashQuery = query(splashRef, orderBy("createdAt", "desc"));

  return onSnapshot(
    splashQuery,
    (snapshot) => {
      const images = snapshot.docs.map(mapDocToImage);
      callback(images);
    },
    (error) => {
      console.error("Error subscribing to launch splash images:", error);
      callback([]);
    }
  );
};

export const getLaunchSplashImages = async (): Promise<LaunchSplashImage[]> => {
  const splashRef = collection(db, COLLECTION);
  const splashQuery = query(splashRef, orderBy("createdAt", "desc"));
  const snapshot = await getDocs(splashQuery);
  return snapshot.docs.map(mapDocToImage);
};

export const uploadLaunchSplashImage = async (file: File) => {
  if (!SUPPORTED_IMAGE_TYPES.includes(file.type)) {
    throw new Error("Unsupported file type. Please use JPG, PNG, WEBP, or GIF images.");
  }

  if (file.size > MAX_FILE_SIZE) {
    throw new Error("File size too large. Maximum size is 5MB.");
  }

  const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
  const fileName = `launch-splash-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${extension}`;
  const storagePath = `launch-splash/${fileName}`;
  const storageRef = ref(storage, storagePath);
  const dimensions = await getImageDimensions(file);

  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);

  return {
    url,
    storagePath,
    fileName,
    width: dimensions?.width ?? null,
    height: dimensions?.height ?? null,
  };
};

export const createLaunchSplashImage = async (params: {
  file: File;
  altText?: string;
  routePath?: string;
  createdBy?: string | null;
}): Promise<void> => {
  const { file, altText, createdBy } = params;
  const uploadResult = await uploadLaunchSplashImage(file);
  const normalizedRoute =
    params.routePath?.trim() && params.routePath.trim() !== "/"
      ? params.routePath.trim().startsWith("/")
        ? params.routePath.trim()
        : `/${params.routePath.trim()}`
      : null;

  await addDoc(collection(db, COLLECTION), {
    url: uploadResult.url,
    storagePath: uploadResult.storagePath,
    fileName: uploadResult.fileName,
    width: uploadResult.width,
    height: uploadResult.height,
    altText: altText || null,
    routePath: normalizedRoute,
    createdBy: createdBy || null,
    status: "inactive",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const setLaunchSplashImageStatus = async (
  imageId: string,
  status: LaunchSplashStatus
): Promise<void> => {
  const splashRef = collection(db, COLLECTION);
  const snapshot = await getDocs(splashRef);

  if (snapshot.empty) {
    throw new Error("No launch splash images found");
  }

  const batch = writeBatch(db);
  let targetFound = false;

  snapshot.forEach((docSnap) => {
    const docRef = doc(db, COLLECTION, docSnap.id);

    if (docSnap.id === imageId) {
      targetFound = true;
      batch.update(docRef, {
        status,
        updatedAt: serverTimestamp(),
      });
    } else if (status === "active") {
      batch.update(docRef, {
        status: "inactive",
        updatedAt: serverTimestamp(),
      });
    }
  });

  if (!targetFound) {
    throw new Error("Launch splash image not found");
  }

  await batch.commit();
};

export const deleteLaunchSplashImage = async (image: LaunchSplashImage): Promise<void> => {
  if (image.storagePath) {
    try {
      const storageRef = ref(storage, image.storagePath);
      await deleteObject(storageRef);
    } catch (error) {
      console.warn("Failed to delete launch splash image asset:", error);
    }
  }

  await deleteDoc(doc(db, COLLECTION, image.id));
};
